import axiosInstance from '../helpers/api';

export interface Deal {
  id: number;
  listing_id: string;
  address?: string;
  unparsed_address?: string;
  city: string;
  postal_code: string;
  list_price: number;
  latitude: number;
  longitude: number;
  bedrooms: number;
  bathrooms: number;
  living_area: number;
  year_built: number;
  days_on_market: number;
  flip_grade: string;
  flip_roi: number;
  flip_profit: number;
  renovated_arv: number;
  as_is_value: number;
  reno_estimate: number;
  mao_20pct: number;
  mao_gap_pct: number;
  distress_score: number;
  is_fixer_upper: boolean;
  deal_status: string;
  scan_date: string;
  change_type: string;
  total_comps: number;
}

export interface DealDetail extends Deal {
  pool_yn?: boolean;
  lot_size_sqft?: number;
  property_subtype?: string;
  county?: string;
  subdivision_name?: string;
  original_list_price?: number;
  predicted_value?: number;
  prediction_confidence?: string;
  price_gap_pct?: number;
  price_gap_dollars?: number;
  deal_score?: number;
  renovated_comps?: number;
  as_is_comps?: number;
  neutral_comps?: number;
  comp_radius_miles?: number;
  garage_spaces?: number;
  holding_costs?: number;
  selling_costs?: number;
  total_all_in?: number;
  mao_15pct?: number;
  price_reduction_pct?: number;
  distress_signals?: string;
  top_renovated_comps?: any[];
  top_as_is_comps?: any[];
  assigned_to?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface DealFilters {
  page?: number;
  per_page?: number;
  search?: string;
  grade?: string;
  min_roi?: number;
  max_price?: number;
  city?: string;
  zip?: string;
  status?: string;
  fixer_only?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface DealSummary {
  grade_a_plus: number;
  grade_a: number;
  grade_b: number;
  grade_c: number;
  grade_d?: number;
  grade_f?: number;
  avg_roi: number;
  total_profit_potential: number;
  total_deals: number;
  cities_count?: number;
  zip_codes_count?: number;
}

export interface DealsResponse {
  success: boolean;
  data: {
    deals: Deal[];
    pagination: {
      page: number;
      per_page: number;
      total_deals: number;
      total_pages: number;
    };
    summary: DealSummary;
  };
}

export interface MLSData {
  listing_id: string;
  unparsed_address: string;
  list_price: number;
  original_list_price: number;
  living_area: number;
  lot_size_sqft: number;
  bedrooms: number;
  bathrooms: number;
  year_built: number;
  garage_spaces: number;
  pool_yn: boolean;
  days_on_market: number;
  standard_status: string;
  public_remarks: string;
  listing_agent_full_name: string;
  listing_office_name: string;
  subdivision_name: string;
  property_subtype: string;
  latitude: number;
  longitude: number;
  photos: string[];
  images?: string[]; // Alternative field name from MLS API
  city: string;
  postal_code: string;
}

class DealService {
  /**
   * Get paginated, filtered list of deal opportunities
   */
  async getDeals(filters: DealFilters = {}): Promise<DealsResponse> {
    const params = new URLSearchParams();
    
    // Add filters to params
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.grade) params.append('grade', filters.grade);
    if (filters.min_roi) params.append('min_roi', filters.min_roi.toString());
    if (filters.max_price) params.append('max_price', filters.max_price.toString());
    if (filters.city) params.append('city', filters.city);
    if (filters.zip) params.append('zip', filters.zip);
    if (filters.status) params.append('status', filters.status);
    if (filters.fixer_only !== undefined) params.append('fixer_only', filters.fixer_only.toString());
    if (filters.sort) params.append('sort', filters.sort);
    if (filters.order) params.append('order', filters.order);
    
    const response = await axiosInstance.get(`/deals/opportunities?${params.toString()}`);
    return response.data;
  }

  /**
   * Get detailed view of a single deal
   */
  async getDealDetail(dealId: number): Promise<{ success: boolean; data: { deal: DealDetail; comp_details: any[] } }> {
    const response = await axiosInstance.get(`/deals/opportunities/${dealId}`);
    return response.data;
  }

  /**
   * Get MLS data for a deal
   */
  async getDealMLSData(dealId: number): Promise<{ success: boolean; data: MLSData }> {
    const response = await axiosInstance.get(`/deals/opportunities/${dealId}/mls`);
    return response.data;
  }

  /**
   * Update deal status
   */
  async updateDealStatus(dealId: number, status: string, assignedTo?: string, notes?: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.patch(`/deals/opportunities/${dealId}/status`, {
      deal_status: status,
      assigned_to: assignedTo,
      notes: notes
    });
    return response.data;
  }

  /**
   * Get dashboard summary statistics
   */
  async getSummary(): Promise<{ success: boolean; data: any }> {
    const response = await axiosInstance.get('/deals/summary');
    return response.data;
  }

  /**
   * Get lightweight map data
   */
  async getMapData(filters: DealFilters = {}): Promise<{ success: boolean; data: any[] }> {
    const params = new URLSearchParams();
    
    // Add filters to params
    if (filters.grade) params.append('grade', filters.grade);
    if (filters.min_roi) params.append('min_roi', filters.min_roi.toString());
    if (filters.max_price) params.append('max_price', filters.max_price.toString());
    if (filters.city) params.append('city', filters.city);
    if (filters.zip) params.append('zip', filters.zip);
    if (filters.status) params.append('status', filters.status);
    if (filters.fixer_only !== undefined) params.append('fixer_only', filters.fixer_only.toString());
    
    const response = await axiosInstance.get(`/deals/map?${params.toString()}`);
    return response.data;
  }

  /**
   * Get grade color for visual display
   */
  getGradeColor(grade: string): string {
    switch (grade) {
      case 'A+': return '#dc3545'; // Red - Hot deal
      case 'A': return '#fd7e14';  // Orange
      case 'B': return '#ffc107';  // Yellow
      case 'C': return '#28a745';  // Green
      case 'D': return '#007bff';  // Blue
      case 'F': return '#6c757d';  // Gray
      default: return '#6c757d';
    }
  }

  /**
   * Get grade emoji for visual display
   */
  getGradeEmoji(grade: string): string {
    switch (grade) {
      case 'A+': return '🔥';
      case 'A': return '⭐';
      case 'B': return '✨';
      case 'C': return '✓';
      case 'D': return '—';
      case 'F': return '✗';
      default: return '—';
    }
  }

  /**
   * Format currency
   */
  formatCurrency(amount: number | string): string {
    const amountNum = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(amountNum)) return '$0';
    
    if (amountNum >= 1000000) {
      return `$${(amountNum / 1000000).toFixed(2)}M`;
    } else if (amountNum >= 1000) {
      return `$${Math.round(amountNum / 1000)}K`;
    }
    return `$${Math.round(amountNum).toLocaleString()}`;
  }

  /**
   * Format ROI percentage
   */
  formatROI(roi: number | string): string {
    const roiNum = typeof roi === 'string' ? parseFloat(roi) : roi;
    if (isNaN(roiNum)) return '0.0%';
    return `${roiNum.toFixed(1)}%`;
  }
}

export default new DealService();